/**
 * saveData.ts
 * Pure save-data helpers — no storage, no platform calls.
 *
 * Shared by `saveManager.ts` (localStorage-backed). All schema and validation
 * logic lives here so storage callers stay byte-compatible if the backend
 * ever changes.
 *
 * @module saveData
 */

import type { SaveData } from '../types/save';
import type { WorldId } from '../../constants/gameConstants';
import { NOVA_MAX_HP, WORLD_IDS } from '../../constants/gameConstants';
import { CHARACTER_IDS } from '../types/character';
import Logger from '../utils/logger';

/**
 * Storage key for the primary save slot.
 * Intentionally unversioned — schema compatibility is handled by
 * `SCHEMA_VERSION` inside the payload, not by the key name. Migrating
 * in-place avoids orphaned keys when the schema increments.
 */
export const SAVE_KEY = '@starfall_courier:save';

/**
 * Current schema version. Increment when making breaking changes to
 * `SaveData`. `validateSaveData()` returns null for saves with a mismatched
 * version rather than loading corrupt/incompatible data.
 */
export const SCHEMA_VERSION = 1;

/**
 * Return a blank `SaveData` with all progress zeroed.
 * @returns A fresh save ready for a new game.
 */
export function createDefaultSave(): SaveData {
  const worlds: Partial<Record<WorldId, WorldProgressLike>> = {};
  for (const id of WORLD_IDS) {
    worlds[id] = { completed: false, shardGateUnlocked: false };
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    totalScore: 0,
    health: NOVA_MAX_HP,
    maxHealth: NOVA_MAX_HP,
    abilities: {
      canDoubleJump: false,
      canWallSlide: false,
      canDash: false,
      canGravityFlip: false,
    },
    worlds,
    levels: {},
    hasSeenIntro: false,
    selectedCharacter: 'nova',
    savedAt: Date.now(),
  };
}

/** Loose shape used during validation before the payload is cast to SaveData. */
type WorldProgressLike = { completed: boolean; shardGateUnlocked: boolean };

/**
 * Validate a parsed JSON payload and coerce it to `SaveData` if it matches
 * the current schema, otherwise return null. Logs a warning describing the
 * specific reason for rejection so corrupted/old saves are debuggable.
 *
 * Pure — no storage I/O. Safe to call on any untrusted JSON.
 *
 * @param raw The parsed JSON payload (already through `JSON.parse`).
 * @returns The validated `SaveData`, or null if invalid.
 */
export function validateSaveData(raw: unknown): SaveData | null {
  // Guard against stored `null` or non-object values (e.g. the literal string
  // "null" stored). Without this check, accessing `data.schemaVersion` on a
  // null parse result would throw a TypeError later in this function.
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    Logger.warn('SaveData', 'Save payload is not a plain object. Discarding save.');
    return null;
  }
  const data = raw as Partial<SaveData>;

  // Check schema version before deep validation — yields the more informative
  // 'schema mismatch' warning instead of a generic shape-validation failure,
  // and avoids unnecessary iteration over nested objects for old payloads.
  if (!Number.isFinite(data.schemaVersion)) {
    Logger.warn('SaveData', 'Save payload failed shape validation. Discarding save.');
    return null;
  }
  if (data.schemaVersion !== SCHEMA_VERSION) {
    Logger.warn(
      'SaveData',
      `Schema mismatch: expected ${SCHEMA_VERSION}, got ${data.schemaVersion}. Discarding save.`,
    );
    return null;
  }

  // Validate remaining required top-level scalar fields, abilities booleans,
  // and that worlds/levels are plain objects. Deep validation prevents corrupt
  // nested data from being cast to SaveData and causing silent runtime errors.
  const abilities = data.abilities;
  if (
    !Number.isFinite(data.totalScore) ||
    !Number.isFinite(data.health) ||
    !Number.isFinite(data.maxHealth) ||
    !Number.isFinite(data.savedAt) ||
    typeof data.hasSeenIntro !== 'boolean' ||
    typeof data.worlds !== 'object' || Array.isArray(data.worlds) || data.worlds === null ||
    typeof data.levels !== 'object' || Array.isArray(data.levels) || data.levels === null ||
    abilities == null ||
    typeof abilities.canDoubleJump !== 'boolean' ||
    typeof abilities.canWallSlide !== 'boolean' ||
    typeof abilities.canDash !== 'boolean' ||
    typeof abilities.canGravityFlip !== 'boolean'
  ) {
    Logger.warn('SaveData', 'Save payload failed shape validation. Discarding save.');
    return null;
  }

  // Validate each present WorldProgress entry.
  for (const entry of Object.values(data.worlds)) {
    const wp = entry as unknown as Record<string, unknown>;
    if (
      wp == null ||
      typeof wp.completed !== 'boolean' ||
      typeof wp.shardGateUnlocked !== 'boolean'
    ) {
      Logger.warn('SaveData', 'WorldProgress entry failed validation. Discarding save.');
      return null;
    }
  }

  // Validate each present LevelProgress entry.
  for (const entry of Object.values(data.levels)) {
    const lp = entry as unknown as Record<string, unknown>;
    if (
      lp == null ||
      typeof lp.completed !== 'boolean' ||
      !Array.isArray(lp.collectedConstellationShardIds) ||
      !(lp.collectedConstellationShardIds as unknown[]).every((e) => typeof e === 'string') ||
      !Array.isArray(lp.collectedStarSeedIds) ||
      !(lp.collectedStarSeedIds as unknown[]).every((e) => typeof e === 'string') ||
      (lp.lastCheckpointId !== null && typeof lp.lastCheckpointId !== 'string')
    ) {
      Logger.warn('SaveData', 'LevelProgress entry failed validation. Discarding save.');
      return null;
    }
    // DECISION: Backfill collectedHeartFragmentIds for saves created before
    // heart fragments existed. Only backfill when truly absent; reject when
    // it exists but is not a valid string[] (corrupted save).
    if (lp.collectedHeartFragmentIds === undefined) {
      lp.collectedHeartFragmentIds = [];
    } else if (!Array.isArray(lp.collectedHeartFragmentIds)) {
      Logger.warn('SaveData', 'collectedHeartFragmentIds is not an array. Discarding save.');
      return null;
    } else if (
      !(lp.collectedHeartFragmentIds as unknown[]).every((e) => typeof e === 'string')
    ) {
      Logger.warn('SaveData', 'collectedHeartFragmentIds failed validation. Discarding save.');
      return null;
    }
  }

  // DECISION: Backfill selectedCharacter for saves created before character
  // selection existed. Defaults to 'nova' — the original protagonist.
  if (
    typeof data.selectedCharacter !== 'string' ||
    !(CHARACTER_IDS as readonly string[]).includes(data.selectedCharacter)
  ) {
    (data as Record<string, unknown>).selectedCharacter = 'nova';
  }
  return data as SaveData;
}

/**
 * Serialise a SaveData payload to JSON, overriding `schemaVersion` and
 * `savedAt` so callers cannot accidentally persist stale metadata.
 * @param data The save to serialise.
 * @returns A JSON string ready to write to any storage backend.
 */
export function serialiseSaveData(data: SaveData): string {
  return JSON.stringify({ ...data, schemaVersion: SCHEMA_VERSION, savedAt: Date.now() });
}
